import React, { useEffect, useState } from 'react'
import { DataFaker, LOGGER } from '@cicero/data/utils'
import { IFormElementProps, createSyntheticChangeEvent, useAutocompleteConfig } from '@cicero/ui/forms'
import { Autocomplete, Chip, Tag, useTheme } from '@cicero/ui'
import { AttributeDoc, AttributeOptionDoc, AttributeOptionSchema, AttributeType, AttributeValueDoc, useModel } from 'models'

export const AttributeOptionChooser = ({
  attribute,
  name,
  onChange,
  selectedValues,
  value,
  ...restOfProps
}: IAttributeOptionChooserProps) => {
  const Options = useModel(AttributeOptionSchema)
  const theme = useTheme()
  const [options, setOptions] = useState<AttributeOptionDoc[]>([])
  const [validAttribute, setValidAttribute] = useState(false)

  useEffect(() => {
    if (attribute && attribute.type === AttributeType.option) {
      setValidAttribute(true)
      const sub = Options.QUERY$({ selector: { attribute_id: attribute.id } })
        .subscribe(opts => {
          setOptions(opts)
          if (selectedValues && selectedValues.length) {
            const index = AttributeOptionSchema.UTILS.indexBy(opts, 'id', true)
            const selected: AttributeOptionDoc[] = []
            selectedValues.forEach((val) => {
              const opt = index[val.option_id || '']
              if (opt) { selected.push(opt) }
            })
            onChange(createSyntheticChangeEvent(name, selected))
          }
        })
      return () => sub.unsubscribe()
    } else {
      setValidAttribute(false)
    }
  }, [attribute?.id])

  return <Autocomplete
    {...useAutocompleteConfig(validAttribute
      ? restOfProps
      : { ...restOfProps, disabled: true, helperText: 'Provide a Valid Attribute' }
    )}
    clearOnBlur
    disableCloseOnSelect={attribute?.max === 1}
    freeSolo
    handleHomeEndKeys
    multiple
    noOptionsText="No Options"
    options={options}
    selectOnFocus
    value={value}
    getOptionLabel={(o) => {
      return o.value || 'No Option'
    }}
    filterOptions={(opts, params) => {
      if (params.inputValue === '') { return opts }
      const filtered = [...opts]
      filtered.push({
        action: params.inputValue,
        label: `Add New Option: "${params.inputValue}"`,
      } as any)
      return filtered
    }}
    renderTags={(opts, getTagProps) => {
      return opts.map((opt, index) => <Chip
        {...getTagProps({ index })}
        key={opt.id}
        label={opt.value}
        style={{ background: opt.color, color: theme.palette.getContrastText(opt.color) }}
      />)
    }}
    renderOption={(o: AttributeOptionDoc) => {
      if ((o as any)['action']) {
        const opt: IOptionAction = o as any
        return <span>{opt.label}</span>
      }
      return <AttributeOption option={o} />
    }}
    onChange={async (event, newValue) => {
      if (newValue === null) {
        // Do Nothing, just set it
      } else if (Array.isArray(newValue)) {
        for (let i = 0; i < newValue.length; i++) {
          const { action } = newValue[i] as any
          if (action && attribute?.id) { // Creating a new Option
            const v = await Options.CREATE({
              value: action,
              attribute_id: attribute.id,
              color: DataFaker.gen(DataFaker.HEXES, DataFaker.genSeed(action)),
              order: Math.max(0, ...options.map(opt => opt.order || 0)) + 1,
            })
            newValue[i] = v
          }
        }
      } else {
        LOGGER.debug('Is not null or array!', newValue)
      }

      onChange(createSyntheticChangeEvent(name, newValue))
    }}
  />
}

export const AttributeOptionRenderer = ({ attribute, values }: { attribute: AttributeDoc, values: AttributeValueDoc[] }) => {
  const Options = useModel(AttributeOptionSchema)
  const [optionsIndex, setOptionIndex] = useState<{ [uuid: string]: AttributeOptionDoc }>({})

  useEffect(() => {
    const sub = Options.QUERY$({ selector: { attribute_id: attribute.id } })
      .subscribe((opts) => {
        setOptionIndex(AttributeOptionSchema.UTILS.indexBy(opts, 'id', true))
      })
    return () => sub.unsubscribe()
  }, [attribute?.id])

  return <>
    { values.map(((val, i) => {
      const option: AttributeOptionDoc = optionsIndex[val.option_id || '']
      return option ? <AttributeOption key={i} option={option} /> : <span key={i}></span>
    })) }
  </>
}

export const AttributeOption = ({ option }: { option: AttributeOptionDoc }) => {
  return <Tag title={option.value} color={option.color} />
}

interface IOptionAction {
  action: string
  label: string
}

interface IAttributeOptionChooserProps extends IFormElementProps<AttributeOptionDoc[]> {
  alwaysOpen?: boolean
  attribute?: AttributeDoc|null
  autoFocus?: boolean
  label?: string
  selectedValues?: AttributeValueDoc[]
}
